import { useParams, useNavigate } from "react-router-dom";
import ManageEducation from "./ManageEducation";
import ManageIntroduction from "./ManageIntroduction";
import ManageWorkExperience from "./ManageWorkExperience";
import { useEffect, useState, useCallback } from "react";
import { URLS } from "../../../../services/urls";
import { API } from "../../../../services/axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ManageCertification from "./ManageCertification";
import ManageLanguages from "./ManageLanguages";
import ManageReferences from "./ManageReferences";
import { PiIdentificationCardLight, PiCertificateLight } from "react-icons/pi";
import { IoSchoolOutline, IoTimeOutline, IoLanguageOutline } from "react-icons/io5";
import { BsPersonCheck } from "react-icons/bs";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ResumePDF from "./ResumePDF";
import { useSelector } from "react-redux";

const STEPS = [
  { key: "intro",       labelKey: "textIntroduction",  Icon: PiIdentificationCardLight },
  { key: "education",   labelKey: "titleEducation",     Icon: IoSchoolOutline },
  { key: "experience",  labelKey: "workExperience",     Icon: IoTimeOutline },
  { key: "certif",      labelKey: "textCertifications", Icon: PiCertificateLight },
  { key: "languages",   labelKey: "textLanguages",      Icon: IoLanguageOutline },
  { key: "references",  labelKey: "textReferences",     Icon: BsPersonCheck },
];

const ManageResume = () => {
  const [loading, setLoading] = useState(false);
  const [resume, setResume]   = useState(null);
  const [step, setStep]       = useState(0);
  const { action }            = useParams();
  const { t }                 = useTranslation();
  const navigate              = useNavigate();
  const isCreate              = action === "create";
  const { auth }              = useSelector((state) => state.auth);

  // ─── Fetch profile (metadata + nested data for PDF) ───────────────────────
  const fetchResume = useCallback(() => {
    if (isCreate) return;
    setLoading(true);
    API.get(URLS.USERS.PROFILE.details(action))
      .then(({ data }) => { setResume(data); setLoading(false); })
      .catch((err) => { toast.error(err?.response?.data?.detail); setLoading(false); });
  }, [action, isCreate]);

  // Fetch on mount
  useEffect(() => { fetchResume(); }, [fetchResume]);

  // Refresh profile metadata whenever the user changes step (keeps PDF up to date)
  useEffect(() => {
    if (!isCreate && step > 0) fetchResume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ─── Navigation ───────────────────────────────────────────────────────────
  const goTo = (idx) => {
    if (isCreate && idx > 0) return;
    setStep(idx);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNextStep = () => goTo(Math.min(step + 1, STEPS.length - 1));
  const handlePrevStep = () => goTo(Math.max(step - 1, 0));

  const handleProfileCreated = (newId) => {
    navigate(`/dashboard/my-resumes/manage/${newId}`);
  };

  // ─── PDF filename ──────────────────────────────────────────────────────────
  const fullName = [auth?.user?.first_name, auth?.user?.last_name]
    .filter(Boolean).join("_") || auth?.user?.email || "cv";

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="box-content">
      <div className="box-heading">
        <div className="box-title">
          <h3 className="mb-35">{t("myResume")}</h3>
        </div>
        <div className="box-breadcrumb">
          <div className="breadcrumbs">
            <ul>
              <li><a className="icon-home" href="/">{t("menuHome")}</a></li>
              <li><span>{t("myResume")}</span></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="section-box">
            <div className="container">

              {/* ── Step tabs ─────────────────────────────────────────── */}
              <div className="resume-steps-nav">
                {STEPS.map(({ key, labelKey, Icon }, idx) => {
                  const disabled = isCreate && idx > 0;
                  const active   = step === idx;
                  return (
                    <button
                      key={key}
                      disabled={disabled}
                      onClick={() => goTo(idx)}
                      className={
                        "resume-step-btn" +
                        (active   ? " resume-step-btn--active"   : "") +
                        (disabled ? " resume-step-btn--disabled" : "")
                      }
                      title={disabled ? t("textSaveIntroFirst") : ""}
                    >
                      <span className="resume-step-icon"><Icon size={16} /></span>
                      <span className="resume-step-label">{t(labelKey)}</span>
                    </button>
                  );
                })}

                {/* PDF button — only in edit mode, shown when profile is loaded */}
                {!isCreate && resume && (
                  <PDFDownloadLink
                    document={<ResumePDF resume={resume} user={auth?.user} />}
                    fileName={`CV_${fullName}.pdf`}
                    className="resume-step-btn resume-step-btn--pdf"
                    style={{ textDecoration: "none" }}
                  >
                    {({ loading: pdfLoading }) =>
                      pdfLoading ? t("textGeneratingPdf") : `⬇  ${t("textViewMyResume")}`
                    }
                  </PDFDownloadLink>
                )}
              </div>

              {/* ── Loading indicator ─────────────────────────────────── */}
              {loading && (
                <div className="d-flex mb-20">
                  <div className="spinner-border m-auto spinner-border-md" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {/* ── Step panels ───────────────────────────────────────── */}
              {step === 0 && (
                <ManageIntroduction
                  resume={resume}
                  nextStep={handleNextStep}
                  handleRefetch={fetchResume}
                  onProfileCreated={handleProfileCreated}
                />
              )}

              {/* Steps 1-4: self-sufficient, no resume/handleRefetch props */}
              {step === 1 && (
                <ManageEducation
                  handleNext={handleNextStep}
                  handlePrev={handlePrevStep}
                />
              )}
              {step === 2 && (
                <ManageWorkExperience
                  handleNext={handleNextStep}
                  handlePrev={handlePrevStep}
                />
              )}
              {step === 3 && (
                <ManageCertification
                  handleNext={handleNextStep}
                  handlePrev={handlePrevStep}
                />
              )}
              {step === 4 && (
                <ManageLanguages
                  handleNext={handleNextStep}
                  handlePrev={handlePrevStep}
                />
              )}

              {/* Step 5: References still receives resume for PDF download */}
              {step === 5 && (
                <ManageReferences
                  resume={resume}
                  handleNext={handleNextStep}
                  handlePrev={handlePrevStep}
                />
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageResume;
